/**
 * The master config editor's push (user, 21/10/2026: "in the master config
 * edit I have a general button in the header saying override all
 * containers … on each value if there's an override, click it to override
 * this specific value for each container"). One context for the header
 * button and every value's marker, and the one confirm they open. Off in a
 * container's editor and on a master not saved yet.
 * For Media Devoted | Created By Ciro Autiero
 */
import { createContext, FC, ReactNode, useContext, useState } from 'react';
import { DROP_GEN_SECTIONS, type DropGenSection } from '@trafficpulse/shared';

import { useAuth } from '@/providers/AuthProvider';

import type { DropGenPlanDetail } from '../../../../drop-generator.types';
import { followersOf } from '../../../campaign/master/rule-facts';
import { switchesGroupOn } from '../../autosend/autosend-slice';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { PushConfirmDialog } from './PushConfirmDialog';
import { type PushOwner, pushOwners } from './push-owners';
import { useMasterPushRun } from './use-master-push-run';

export interface MasterPush {
  active: boolean;
  masterName: string;
  /** The containers a push of this section (every section when absent) would change. */
  owners: (section?: DropGenSection) => PushOwner[];
  /** Why that push cannot run right now; null = it can. */
  blocked: (section?: DropGenSection) => string | null;
  ask: (section?: DropGenSection) => void;
}

const OFF: MasterPush = { active: false, masterName: '', owners: () => [], blocked: () => null, ask: () => undefined };
const Ctx = createContext<MasterPush>(OFF);
const NOT_AUTOSEND = DROP_GEN_SECTIONS.filter((s) => s !== 'autoSend');
const keepsGateways = (s?: DropGenSection) => !s || s === 'gatewayPool' || s === 'gatewayStrategy';

interface Props {
  plan: DropGenPlanDetail;
  /** The saved master config open in the editor; null = no push (a container, or a new master). */
  masterId: string | null;
  editor: ContainerEditor;
  edits: { dirty: boolean; invalidReason: string | null; save: () => Promise<boolean> };
  children: ReactNode;
}

export const MasterPushProvider: FC<Props> = ({ plan, masterId, editor, edits, children }) => {
  const { hasPermission } = useAuth();
  const canAutoSend = hasPermission('drop_generator.autosend');
  const { draft } = editor;
  const masterName = draft.name.trim() || 'the master config';
  const [asking, setAsking] = useState<{ section?: DropGenSection } | null>(null);
  const runner = useMasterPushRun({ masterId, masterName, countryKey: plan.countryKey, saveFirst: () => (edits.dirty ? edits.save() : Promise.resolve(true)) });
  // Without the auto-send permission, "all" leaves the containers' auto-send layers (the API refuses them).
  const scope = (section?: DropGenSection) => (section ? [section] : canAutoSend ? undefined : NOT_AUTOSEND);
  const owners = (section?: DropGenSection) => (masterId ? pushOwners(plan, masterId, scope(section)) : []);
  const blocked = (section?: DropGenSection) => {
    if (section === 'autoSend' && !canAutoSend) return 'Taking a container’s own auto-send away needs the auto-send permission';
    if (edits.dirty && edits.invalidReason) return `${edits.invalidReason} — then save`;
    if (edits.dirty && switchesGroupOn(draft)) return 'Save the master config first — it switches auto-send on';
    return null;
  };
  const followers = masterId ? followersOf(plan, masterId) : [];
  const value: MasterPush = { active: Boolean(masterId), masterName, owners, blocked, ask: (section) => setAsking({ section }) };
  return (
    <Ctx.Provider value={value}>
      {children}
      <PushConfirmDialog
        open={asking !== null}
        section={asking?.section}
        owners={asking ? owners(asking.section) : []}
        masterName={masterName}
        values={draft}
        notes={{
          savesFirst: edits.dirty,
          routeTestsKeep: keepsGateways(asking?.section) && followers.some((c) => c.kind === 'ROUTE_TEST' && (c.ownSections.gatewayPool || c.ownSections.gatewayStrategy)),
          autoSendStays: !asking?.section && !canAutoSend && followers.some((c) => c.ownSections.autoSend),
        }}
        busy={runner.busy}
        onConfirm={() => void runner.run(scope(asking?.section)).then((done) => done && setAsking(null))}
        onCancel={() => setAsking(null)}
      />
    </Ctx.Provider>
  );
};

export const useMasterPush = () => useContext(Ctx);
